import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, CreditCard, Search, User, Upload, Camera, FileUp, Printer, UserPlus, Link2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminStudents = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [prefillUser, setPrefillUser] = useState(null);
  const [pending, setPending] = useState({ pending_users: [], pending_profiles: [] });
  
  useEffect(() => {
    fetchStudents();
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    filterStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedDiscipline, students]);
  
  const fetchStudents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students`, {
        credentials: 'include'
      });
      const data = await response.json();
      setStudents(data);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Error al cargar alumnos');
    }
  };
  
  const fetchPending = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pending-students`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPending(data);
      }
    } catch (error) {
      console.error('Error fetching pending:', error);
    }
  };

  const handleActivatePendingUser = (pendingUser) => {
    setPrefillUser(pendingUser);
    setIsCreateDialogOpen(true);
  };

  const handleRelinkAll = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/relink-students`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Vinculados: ${data.relinked} perfil(es)`);
        fetchStudents();
        fetchPending();
      }
    } catch (error) {
      console.error('Error relinking:', error);
      toast.error('Error al vincular alumnos');
    }
  };
  
  const filterStudents = () => {
    let filtered = students;
    
    if (searchQuery) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (selectedDiscipline !== 'all') {
      filtered = filtered.filter(s => s.discipline === selectedDiscipline);
    }
    
    setFilteredStudents(filtered);
  };
  
  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este alumno?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students/${studentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Alumno eliminado');
        fetchStudents();
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Error al eliminar alumno');
    }
  };
  
  const handlePrintCard = async (studentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students/${studentId}/card`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to generate card');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Carnet generado');
    } catch (error) {
      console.error('Print card error:', error);
      toast.error('Error al generar carnet');
    }
  };
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-heading text-white uppercase">ALUMNOS</h1>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => navigate('/admin/import')}
              data-testid="import-button"
              className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm font-medium"
            >
              <FileUp className="mr-2 h-4 w-4" />
              IMPORTAR
            </Button>
            
            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
              setIsCreateDialogOpen(open);
              if (!open) setPrefillUser(null);
            }}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-student-button"
                  className="bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase tracking-wider shadow-[0_0_15px_rgba(229,9,20,0.3)]"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  NUEVO
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-950 border-zinc-800 text-white">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl uppercase">Nuevo Alumno</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    {prefillUser ? `Activando a ${prefillUser.name}` : 'Completa la información del alumno'}
                  </DialogDescription>
              </DialogHeader>
              <StudentForm
                prefillUser={prefillUser}
                onSuccess={() => {
                  setIsCreateDialogOpen(false);
                  setPrefillUser(null);
                  fetchStudents();
                  fetchPending();
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="max-w-7xl mx-auto p-4">
        <Card className="bg-zinc-950 border-zinc-800 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Buscar alumno..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-800 text-white"
                data-testid="search-input"
              />
            </div>
            
            <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="discipline-filter">
                <SelectValue placeholder="Filtrar por disciplina" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                <SelectItem value="all">Todas las disciplinas</SelectItem>
                <SelectItem value="Kickboxing">Kickboxing</SelectItem>
                <SelectItem value="Boxeo">Boxeo</SelectItem>
                <SelectItem value="Muay Thai">Muay Thai</SelectItem>
                <SelectItem value="Jiu Jitsu">Jiu Jitsu</SelectItem>
                <SelectItem value="Grappling">Grappling</SelectItem>
                <SelectItem value="MMA">MMA</SelectItem>
                <SelectItem value="Lucha Libre">Lucha Libre</SelectItem>
                <SelectItem value="CrossFit">CrossFit</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>
      </div>

      {/* Pending Users Panel: Google users that need a student profile */}
      {pending.pending_users.length > 0 && (
        <div className="max-w-7xl mx-auto px-4">
          <Card className="bg-amber-950/30 border-amber-800/50 p-4" data-testid="pending-users-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-amber-400" />
                <h2 className="text-sm font-heading text-amber-200 uppercase tracking-wider">
                  Usuarios pendientes de activar ({pending.pending_users.length})
                </h2>
              </div>
              <p className="text-xs text-amber-300/70 hidden sm:block">
                Iniciaron sesión con Google pero aún no tienen perfil de alumno
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {pending.pending_users.map((pu) => (
                <div
                  key={pu.user_id}
                  className="flex items-center justify-between bg-zinc-900 border border-amber-900/40 rounded-sm p-3"
                  data-testid={`pending-user-${pu.user_id}`}
                >
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    {pu.picture ? (
                      <img src={pu.picture} alt={pu.name} className="w-9 h-9 rounded-sm object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-sm bg-zinc-800 flex items-center justify-center">
                        <User className="h-4 w-4 text-zinc-500" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{pu.name}</p>
                      <p className="text-xs text-zinc-400 truncate">{pu.email}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleActivatePendingUser(pu)}
                    data-testid={`activate-user-${pu.user_id}`}
                    className="bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase text-xs ml-2 shrink-0"
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Activar
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Orphan Profiles Panel: Imported/manually-created students waiting for owner to log in */}
      {pending.pending_profiles.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 mt-3">
          <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="orphan-profiles-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Link2 className="h-5 w-5 text-zinc-400" />
                <h2 className="text-sm font-heading text-zinc-300 uppercase tracking-wider">
                  Alumnos sin cuenta vinculada ({pending.pending_profiles.length})
                </h2>
              </div>
              <Button
                size="sm"
                onClick={handleRelinkAll}
                data-testid="relink-all-button"
                className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm text-xs"
              >
                <Link2 className="h-3 w-3 mr-1" />
                Vincular automáticamente
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mb-2">
              Estos alumnos fueron importados o creados manualmente. Se vincularán cuando su dueño inicie sesión con Google (o con el botón de arriba si ya lo hicieron).
            </p>
            <div className="flex flex-wrap gap-2">
              {pending.pending_profiles.slice(0, 20).map((pp) => (
                <span
                  key={pp.student_id}
                  className="text-xs px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-sm text-zinc-300"
                >
                  {pp.name}{pp.email ? ` · ${pp.email}` : ''}
                </span>
              ))}
              {pending.pending_profiles.length > 20 && (
                <span className="text-xs text-zinc-500">+ {pending.pending_profiles.length - 20} más</span>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Students Grid */}
      <div className="max-w-7xl mx-auto p-4 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStudents.map((student) => (
            <Card key={student.student_id} className="bg-zinc-950 border-zinc-800 p-4 hover:border-zinc-700 transition-colors" data-testid={`student-card-${student.student_id}`}>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  {student.photo_url ? (
                    <img 
                      src={student.photo_url}
                      alt={student.name}
                      className="w-16 h-16 rounded-sm object-cover border-2 border-zinc-700"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-sm bg-zinc-800 flex items-center justify-center">
                      <User className="h-8 w-8 text-zinc-600" />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-heading text-white uppercase">{student.name}</h3>
                    <p className="text-sm text-zinc-400">{student.discipline}</p>
                    {student.grade && (
                      <p className="text-xs text-zinc-500">Grado: {student.grade}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                  <span className="text-xs text-zinc-500 uppercase">{student.plan_type}</span>
                  
                  <div className="flex space-x-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePrintCard(student.student_id)}
                      data-testid={`print-button-${student.student_id}`}
                      title="Imprimir carnet"
                    >
                      <Printer className="h-4 w-4 text-green-400" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedStudent(student);
                        setIsPhotoDialogOpen(true);
                      }}
                      data-testid={`photo-button-${student.student_id}`}
                      title="Subir foto"
                    >
                      <Camera className="h-4 w-4 text-blue-400" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedStudent(student);
                        setIsPaymentDialogOpen(true);
                      }}
                      data-testid={`payment-button-${student.student_id}`}
                    >
                      <CreditCard className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedStudent(student);
                        setIsEditDialogOpen(true);
                      }}
                      data-testid={`edit-button-${student.student_id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteStudent(student.student_id)}
                      data-testid={`delete-button-${student.student_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-500">No se encontraron alumnos</p>
          </div>
        )}
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Editar Alumno</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <StudentForm 
              student={selectedStudent}
              onSuccess={() => {
                setIsEditDialogOpen(false);
                setSelectedStudent(null);
                fetchStudents();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Pagos · {selectedStudent?.name}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Registra un nuevo pago o edita pagos previos (ej. inscripciones antiguas)
            </DialogDescription>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-6">
              <PaymentForm
                student={selectedStudent}
                onSuccess={() => {
                  toast.success('Pago registrado exitosamente');
                }}
              />
              <PaymentHistoryEditor student={selectedStudent} />
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Photo Upload Dialog */}
      <Dialog open={isPhotoDialogOpen} onOpenChange={setIsPhotoDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Subir Foto</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedStudent?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedStudent && (
            <PhotoUploadForm 
              student={selectedStudent}
              onSuccess={() => {
                setIsPhotoDialogOpen(false);
                setSelectedStudent(null);
                fetchStudents();
                toast.success('Foto subida exitosamente');
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Student Form Component
const StudentForm = ({ student, prefillUser, onSuccess }) => {
  const toDateInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    } catch (_e) {
      return '';
    }
  };

  const [formData, setFormData] = useState({
    email: student?.email || prefillUser?.email || '',
    name: student?.name || prefillUser?.name || '',
    photo_url: student?.photo_url || '',
    discipline: student?.discipline || 'Kickboxing',
    grade: student?.grade || '',
    blood_type: student?.blood_type || '',
    emergency_contact: student?.emergency_contact || '',
    emergency_phone: student?.emergency_phone || '',
    plan_type: student?.plan_type || 'Gold',
    enrollment_date: toDateInput(student?.enrollment_date || student?.created_at)
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = student
        ? `${process.env.REACT_APP_BACKEND_URL}/api/students/${student.student_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/students`;

      const method = student ? 'PUT' : 'POST';

      // Build payload: convert enrollment_date to ISO datetime if present
      const payload = { ...formData };
      if (payload.enrollment_date) {
        // Preserve time-of-day as noon UTC to avoid timezone shifts
        payload.enrollment_date = new Date(`${payload.enrollment_date}T12:00:00Z`).toISOString();
      } else {
        delete payload.enrollment_date;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(student ? 'Alumno actualizado' : 'Alumno creado');
        onSuccess();
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save student');
      }
    } catch (error) {
      console.error('Error saving student:', error);
      toast.error(error.message || 'Error al guardar alumno');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div>
        <Label className="text-white">Email *</Label>
        <Input
          required
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="bg-zinc-900 border-zinc-800 text-white"
          placeholder="alumno@gmail.com"
          data-testid="student-email-input"
          disabled={!!prefillUser}
        />
        <p className="text-xs text-zinc-500 mt-1">
          {prefillUser
            ? 'Se vinculará automáticamente a la cuenta de Google del alumno'
            : 'Usa el correo con el que el alumno iniciará sesión en Google'}
        </p>
      </div>
      
      <div>
        <Label className="text-white">Nombre *</Label>
        <Input
          required
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="bg-zinc-900 border-zinc-800 text-white"
        />
      </div>
      
      <div>
        <Label className="text-white">URL de Foto</Label>
        <Input
          value={formData.photo_url}
          onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
          className="bg-zinc-900 border-zinc-800 text-white"
          placeholder="https://..."
        />
      </div>
      
      <div>
        <Label className="text-white">Disciplina *</Label>
        <Select value={formData.discipline} onValueChange={(v) => setFormData({...formData, discipline: v})}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
            <SelectItem value="Kickboxing">Kickboxing</SelectItem>
            <SelectItem value="Boxeo">Boxeo</SelectItem>
            <SelectItem value="Muay Thai">Muay Thai</SelectItem>
            <SelectItem value="Jiu Jitsu">Jiu Jitsu</SelectItem>
            <SelectItem value="Grappling">Grappling</SelectItem>
            <SelectItem value="MMA">MMA</SelectItem>
            <SelectItem value="Lucha Libre">Lucha Libre</SelectItem>
            <SelectItem value="CrossFit">CrossFit</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-white">Grado/Khan</Label>
          <Input
            value={formData.grade}
            onChange={(e) => setFormData({...formData, grade: e.target.value})}
            className="bg-zinc-900 border-zinc-800 text-white"
          />
        </div>
        
        <div>
          <Label className="text-white">Tipo de Sangre</Label>
          <Input
            value={formData.blood_type}
            onChange={(e) => setFormData({...formData, blood_type: e.target.value})}
            className="bg-zinc-900 border-zinc-800 text-white"
            placeholder="O+, A-, etc."
          />
        </div>
      </div>
      
      <div>
        <Label className="text-white">Contacto de Emergencia</Label>
        <Input
          value={formData.emergency_contact}
          onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
          className="bg-zinc-900 border-zinc-800 text-white"
        />
      </div>
      
      <div>
        <Label className="text-white">Teléfono de Emergencia</Label>
        <Input
          value={formData.emergency_phone}
          onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
          className="bg-zinc-900 border-zinc-800 text-white"
        />
      </div>
      
      <div>
        <Label className="text-white">Tipo de Plan *</Label>
        <Select value={formData.plan_type} onValueChange={(v) => setFormData({...formData, plan_type: v})}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
            <SelectItem value="Silver">Membresía Silver (3 días/semana)</SelectItem>
            <SelectItem value="Gold">Membresía Gold (1 ingreso/día, todos los días)</SelectItem>
            <SelectItem value="Platinum">Membresía Platinum (2 ingresos/día, todos los días)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-white">Fecha de Inicio</Label>
        <Input
          type="date"
          value={formData.enrollment_date}
          onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
          className="bg-zinc-900 border-zinc-800 text-white"
          data-testid="enrollment-date-input"
        />
        <p className="text-xs text-zinc-500 mt-1">
          Si el alumno se inscribió antes de usar la app, coloca la fecha real de inicio
        </p>
      </div>

      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase"
      >
        {loading ? 'Guardando...' : student ? 'Actualizar' : 'Crear Alumno'}
      </Button>
    </form>
  );
};

// Payment Form Component
const PaymentForm = ({ student, onSuccess }) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState('');
  const [months, setMonths] = useState('1');
  const [paymentDate, setPaymentDate] = useState(todayIso);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        student_id: student.student_id,
        amount: parseFloat(amount),
        months: parseInt(months)
      };
      if (paymentDate) {
        payload.payment_date = new Date(`${paymentDate}T12:00:00Z`).toISOString();
      }
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSuccess();
      } else {
        throw new Error('Failed to register payment');
      }
    } catch (error) {
      console.error('Error registering payment:', error);
      toast.error('Error al registrar pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-white">Monto (Bs) *</Label>
        <Input
          required
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="bg-zinc-900 border-zinc-800 text-white"
          placeholder="200.00"
          data-testid="payment-amount-input"
        />
      </div>

      <div>
        <Label className="text-white">Fecha de Pago</Label>
        <Input
          type="date"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          className="bg-zinc-900 border-zinc-800 text-white"
          data-testid="payment-date-input"
        />
        <p className="text-xs text-zinc-500 mt-1">
          Si el alumno pagó en una fecha anterior, ajústala aquí. La mensualidad se cuenta desde esta fecha (o se extiende si aún tiene mensualidad activa).
        </p>
      </div>

      <div>
        <Label className="text-white">Meses *</Label>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
            <SelectItem value="1">1 mes</SelectItem>
            <SelectItem value="2">2 meses</SelectItem>
            <SelectItem value="3">3 meses</SelectItem>
            <SelectItem value="6">6 meses</SelectItem>
            <SelectItem value="12">12 meses</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase"
      >
        {loading ? 'Registrando...' : 'Registrar Pago'}
      </Button>
    </form>
  );
};

// Photo Upload Form Component
const PhotoUploadForm = ({ student, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(student?.photo_url || null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        toast.error('Tipo de archivo no permitido. Use JPG, PNG, WebP o GIF.');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Archivo muy grande. Máximo 5MB.');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecciona una imagen');
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload/student-photo/${student.student_id}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al subir foto');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Error al subir foto');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Preview */}
      <div className="flex justify-center">
        <div 
          className="w-48 h-48 rounded-sm border-2 border-dashed border-zinc-700 flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          {preview ? (
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center">
              <Upload className="h-12 w-12 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">Click para seleccionar</p>
            </div>
          )}
        </div>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <div className="flex space-x-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="flex-1"
        >
          Seleccionar Imagen
        </Button>
        
        <Button
          type="button"
          onClick={handleUpload}
          disabled={!selectedFile || loading}
          className="flex-1 bg-primary hover:bg-[#b20710] text-white"
        >
          {loading ? 'Subiendo...' : 'Subir Foto'}
        </Button>
      </div>
      
      <p className="text-xs text-zinc-500 text-center">
        Formatos: JPG, PNG, WebP, GIF. Máximo 5MB.
      </p>
    </div>
  );
};

// Payment History Editor – list previous payments, allow inline edit/delete
const PaymentHistoryEditor = ({ student }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editPaymentDate, setEditPaymentDate] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [editAmount, setEditAmount] = useState('');

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/student/${student.student_id}`, {
        credentials: 'include'
      });
      if (r.ok) {
        const data = await r.json();
        setPayments(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // Refresh every 2s in case a new payment was just registered
    const interval = setInterval(fetchPayments, 2500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student.student_id]);

  const startEdit = (p) => {
    setEditingId(p.payment_id);
    setEditPaymentDate(p.payment_date ? p.payment_date.slice(0, 10) : '');
    setEditExpiresAt(p.expires_at ? p.expires_at.slice(0, 10) : '');
    setEditAmount(String(p.amount || ''));
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (paymentId) => {
    try {
      const payload = {};
      if (editAmount) payload.amount = parseFloat(editAmount);
      if (editPaymentDate) payload.payment_date = new Date(`${editPaymentDate}T12:00:00Z`).toISOString();
      if (editExpiresAt) payload.expires_at = new Date(`${editExpiresAt}T12:00:00Z`).toISOString();

      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${paymentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (r.ok) {
        toast.success('Pago actualizado');
        setEditingId(null);
        fetchPayments();
      } else {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Error al actualizar');
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Error al actualizar pago');
    }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm('¿Eliminar este pago? Esta acción no se puede deshacer.')) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${paymentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (r.ok) {
        toast.success('Pago eliminado');
        fetchPayments();
      } else {
        throw new Error('Error al eliminar');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="border-t border-zinc-800 pt-4">
      <h3 className="text-sm font-heading text-zinc-300 uppercase tracking-wider mb-3">
        Historial de Pagos ({payments.length})
      </h3>
      {loading && <p className="text-zinc-500 text-sm">Cargando...</p>}
      {!loading && payments.length === 0 && (
        <p className="text-zinc-500 text-sm">Aún no hay pagos registrados</p>
      )}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {payments.map((p) => (
          <div
            key={p.payment_id}
            className="bg-zinc-900 border border-zinc-800 rounded-sm p-3"
            data-testid={`payment-row-${p.payment_id}`}
          >
            {editingId === p.payment_id ? (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-zinc-400">Pagado el</Label>
                    <Input
                      type="date"
                      value={editPaymentDate}
                      onChange={(e) => setEditPaymentDate(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-white text-xs"
                      data-testid={`edit-payment-date-${p.payment_id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Vence el</Label>
                    <Input
                      type="date"
                      value={editExpiresAt}
                      onChange={(e) => setEditExpiresAt(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-white text-xs"
                      data-testid={`edit-expires-at-${p.payment_id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Monto (Bs)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-white text-xs"
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => saveEdit(p.payment_id)}
                    data-testid={`save-payment-${p.payment_id}`}
                    className="bg-primary hover:bg-[#b20710] text-white text-xs"
                  >
                    Guardar
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={cancelEdit} className="text-xs">
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm text-white">
                    {formatDate(p.payment_date)} → {formatDate(p.expires_at)}
                  </p>
                  <p className="text-xs text-zinc-500">Bs {Number(p.amount).toFixed(2)}</p>
                </div>
                <div className="flex space-x-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => startEdit(p)}
                    data-testid={`edit-payment-${p.payment_id}`}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => deletePayment(p.payment_id)}
                    data-testid={`delete-payment-${p.payment_id}`}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminStudents;
